import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";

/**
 * ThemeToggle – persistent fixed button in the bottom-left corner.
 * Square, rounded, single icon that swaps between Sun and Moon.
 * Mirrors the visual language of the top-right CopyLink button.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    if (typeof document === "undefined") return "dark";
    return document.documentElement.classList.contains("light")
      ? "light"
      : "dark";
  });

  // Amount (px) to shift the fixed button up so it never overlaps the footer.
  const [shift, setShift] = useState(0);

  useEffect(() => {
    const isLight = document.documentElement.classList.contains("light");
    setTheme(isLight ? "light" : "dark");
  }, []);

  // Track distance to footer and lift the button above it on scroll / resize.
  useEffect(() => {
    const footer = document.querySelector('[data-testid="site-footer"]');
    if (!footer) return undefined;
    let raf = 0;

    const update = () => {
      const rect = footer.getBoundingClientRect();
      const gap = 16; // desired gap between button and footer top
      const isDesktop = window.matchMedia("(min-width: 640px)").matches;
      const baseBottom = isDesktop ? 24 : 16; // matches bottom-6 / bottom-4
      // Overlap = how much the fixed button (sitting at baseBottom from viewport
      // bottom) would intrude into the footer once it enters the viewport.
      const overlap =
        window.innerHeight - rect.top - baseBottom + gap;
      setShift(Math.max(0, overlap));
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem("theme", next);
    } catch {
      /* ignore */
    }
    document.documentElement.classList.toggle("light", next === "light");
    document.documentElement.classList.toggle("dark", next === "dark");
  };

  return (
    <div
      className="fixed bottom-4 left-4 z-30 sm:bottom-6 sm:left-6"
      style={{
        transform: `translateY(-${shift}px)`,
        transition: "transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <motion.button
        type="button"
        onClick={toggle}
        whileTap={{ scale: 0.94 }}
        aria-label={
          theme === "dark" ? "Zu Light Mode wechseln" : "Zu Dark Mode wechseln"
        }
        data-testid="theme-toggle-btn"
        className="group flex h-10 w-10 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] text-zinc-300 backdrop-blur-xl transition-all duration-300 hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
      >
        <AnimatePresence mode="wait" initial={false}>
          {theme === "dark" ? (
            <motion.span
              key="sun"
              initial={{ opacity: 0, rotate: -45, scale: 0.7 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: 45, scale: 0.7 }}
              transition={{ duration: 0.25 }}
              className="flex"
              data-testid="theme-toggle-current-sun"
            >
              <Sun size={16} strokeWidth={2} />
            </motion.span>
          ) : (
            <motion.span
              key="moon"
              initial={{ opacity: 0, rotate: 45, scale: 0.7 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: -45, scale: 0.7 }}
              transition={{ duration: 0.25 }}
              className="flex"
              data-testid="theme-toggle-current-moon"
            >
              <Moon size={16} strokeWidth={2} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
